import React, { useEffect, useRef, useState } from "react";
import { useLanguage } from "../../context/LanguageContext";
import {
  Activity,
  Award,
  Banknote,
  Box,
  Building2,
  Bus,
  Car,
  CheckCircle2,
  Factory,
  Globe2,
  Hammer,
  Handshake,
  Hotel,
  Landmark,
  MapPin,
  Mountain,
  Plane,
  ShieldCheck,
  Ship,
  Star,
  Target,
  Wrench,
  Zap,
} from "lucide-react";

function useInView<T extends HTMLElement>(options?: IntersectionObserverInit) {
  const ref = useRef<T | null>(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    if (inView) return;

    const el = ref.current;
    if (!el) return;

    let observer: IntersectionObserver | null = null;

    observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          observer?.disconnect();
        }
      },
      {
        threshold: 0.25,
        rootMargin: "0px 0px -20% 0px",
        ...(options || {}),
      }
    );

    observer.observe(el);
    return () => observer?.disconnect();
  }, [inView, options]);

  return { ref, inView };
}

const Reveal: React.FC<{
  children: React.ReactNode;
  variant?: "fadeUp" | "fadeLeft" | "fadeRight" | "scale";
  delayMs?: number;
  className?: string;
}> = ({ children, variant = "fadeUp", delayMs = 0, className = "" }) => {
  const { ref, inView } = useInView<HTMLDivElement>();
  const delay = delayMs ? { transitionDelay: `${delayMs}ms` } : undefined;

  const hidden: Record<string, string> = {
    fadeUp: "opacity-0 translate-y-6",
    fadeLeft: "opacity-0 -translate-x-6",
    fadeRight: "opacity-0 translate-x-6",
    scale: "opacity-0 scale-95",
  };

  return (
    <div
      ref={ref}
      style={delay}
      className={`${className} transition-all duration-700 ease-out will-change-transform will-change-opacity ${
        inView ? "opacity-100 translate-y-0 translate-x-0 scale-100" : hidden[variant]
      }`}
    >
      {children}
    </div>
  );
};

const iconMap: Record<string, React.ElementType> = {
  Activity,
  Award,
  Banknote,
  Box,
  Building2,
  Bus,
  Car,
  CheckCircle2,
  Factory,
  Globe2,
  Hammer,
  Handshake,
  Hotel,
  Landmark,
  MapPin,
  Mountain,
  Plane,
  ShieldCheck,
  Ship,
  Star,
  Target,
  Wrench,
  Zap,
};

const LinkText: React.FC<{ text: string }> = ({ text }) => (
  <>
    {String(text)
      .split(/((?:https?:\/\/)?www\.[^\s]+)/)
      .map((part, idx) => {
        const href = part.startsWith("www.") ? `http://${part}` : part;
        return part.includes("www.") ? (
          <a
            key={`${part}-${idx}`}
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 hover:underline"
          >
            {part}
          </a>
        ) : (
          <React.Fragment key={`${part}-${idx}`}>{part}</React.Fragment>
        );
      })}
  </>
);

const DivisionIntro: React.FC<{ content?: any }> = ({ content }) => {
  const { t, t_api } = useLanguage();

  const kicker = content?.kicker || t_api("Hamdan Group - Oman", "مجموعة حمدان - عمان");
  const title = content?.title || t_api("Hamdan Group", "مجموعة حمدان");
  const desc = content?.desc || t_api("A Hamdan Group division serving Oman with reliable business services.", "قسم من مجموعة حمدان يقدم خدمات تجارية موثوقة في عمان.");
  const introText = content?.introText || "";
  const image = content?.introImage || content?.image || content?.brandLogo || "/banner/banner4.jpg";
  const imageFit = content?.imageFit || "contain";
  const noteTitle = content?.noteTitle || t_api("Quick Note", "ملاحظة سريعة");
  const noteDesc = content?.noteDesc || t_api("Part of Hamdan Group operations across Oman.", "جزء من عمليات مجموعة حمدان في جميع أنحاء عمان.");
  const imageBadgeTitle = content?.imageBadgeTitle || t('common.intro');
  const imageLabel = content?.imageLabel || title;
  const panelTitle = content?.panelTitle || t('common.intro').toUpperCase();
  const panelFooter = content?.panelFooter === "Details based on company profile content." ? "" : (content?.panelFooter || "");
  const capabilitiesTitle = content?.capabilitiesTitle || t_api("Core Capabilities", "القدرات الأساسية");
  const capabilitiesMeta = content?.capabilitiesMeta || t_api("Key Features", "الميزات الرئيسية");
  const brandDesc = content?.brandDesc || t_api("Focused on reliable execution, practical service, and long-term value.", "التركيز على التنفيذ الموثوق والخدمة العملية والقيمة طويلة الأجل.");
  const brandTags =
    content?.brandTags && content.brandTags.length > 0
      ? content.brandTags.map((tag: string) => t_api(tag))
      : [t_api("Hamdan", "حمدان"), t_api("Oman", "عمان"), t_api("Service", "خدمة")];

  const titleWords = String(title).trim().split(/\s+/);
  const titleFirstLine = titleWords[0] || title;
  const titleSecondLine = titleWords.slice(1).join(" ");

  const points =
    content?.points && content.points.length > 0
      ? content.points
      : [
          {
            title: t_api("Trusted Operations", "عمليات موثوقة"),
            desc: t_api("Established services backed by Hamdan Group experience.", "خدمات راسخة مدعومة بخبرة مجموعة حمدان."),
            icon: "CheckCircle2",
          },
          {
            title: t_api("Customer Focus", "التركيز على العملاء"),
            desc: t_api("Practical support for customers and partners across Oman.", "دعم عملي للعملاء والشركاء في جميع أنحاء عمان."),
            icon: "Handshake",
          },
          {
            title: t_api("Service Coverage", "تغطية الخدمات"),
            desc: t_api("Reliable business presence across key operating locations.", "تواجد تجاري موثوق في مواقع التشغيل الرئيسية."),
            icon: "MapPin",
          },
        ];

  const imageClass =
    imageFit === "cover"
      ? "w-full h-[460px] object-cover"
      : "w-full h-[460px] object-contain p-8 bg-white";

  return (
    <section className="bg-white">
      <div className="max-w-7xl mx-auto px-4 py-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          <Reveal variant="fadeLeft" className="lg:col-span-3">
            <div className="sticky top-6">
              <p className="text-xs uppercase tracking-widest text-gray-500">
                {kicker}
              </p>

              <h2 className="mt-3 font-semibold leading-tight text-gray-900 break-words">
                <span className="block text-[24px] md:text-[28px]">{titleFirstLine}</span>
                {titleSecondLine && (
                  <span className="block text-[30px] md:text-[34px] xl:text-[38px]">
                    {titleSecondLine}
                  </span>
                )}
              </h2>

              <div className="mt-6 space-y-2">
                {points.slice(0, 3).map((point: any, idx: number) => (
                  <div
                    key={`${point.title || "point"}-${idx}`}
                    className="inline-flex items-center gap-2 text-xs font-semibold px-3 py-2 rounded-full border bg-gray-50"
                  >
                    <span className="h-2 w-2 rounded-full bg-yellow-500 shrink-0" />
                    <span>{point.title || point.desc}</span>
                  </div>
                ))}
              </div>

              {noteDesc && (
                <div className="mt-6 rounded-2xl border p-4 bg-gray-50">
                  <div className="text-xs uppercase tracking-wider text-gray-500">
                    {noteTitle}
                  </div>
                  <p className="mt-2 text-sm text-gray-700 leading-relaxed">
                    <LinkText text={noteDesc} />
                  </p>
                </div>
              )}
            </div>
          </Reveal>

          <Reveal variant="scale" delayMs={120} className="lg:col-span-5">
            <div className="relative rounded-[28px] overflow-hidden border shadow-sm bg-gray-50">
              <img
                src={image}
                alt={title}
                className={imageClass}
                onError={(event) => {
                  event.currentTarget.src = "/banner/banner4.jpg";
                }}
              />

              <div className="absolute left-5 top-5 bg-white/90 backdrop-blur px-4 py-3 rounded-2xl border shadow-sm">
                <div className="text-xs uppercase tracking-widest text-gray-600">
                  {imageBadgeTitle}
                </div>
                <div className="text-sm font-semibold text-gray-900">
                  {imageLabel}
                </div>
              </div>

              <div className="absolute inset-x-0 bottom-0 h-2 bg-gradient-to-r from-yellow-500 via-yellow-400 to-red-500" />
            </div>
          </Reveal>

          <Reveal variant="fadeRight" delayMs={200} className="lg:col-span-4">
            <div className="rounded-2xl border bg-gray-50 p-6">
              <h3 className="text-lg font-bold text-gray-900">
                {panelTitle}
              </h3>

              <div className="mt-4 space-y-3 text-sm text-gray-700 leading-relaxed text-justify">
                <div
                  dangerouslySetInnerHTML={{
                    __html: String(desc).replace(/\n/g, "<br/>"),
                  }}
                />
                {introText && (
                  <div
                    dangerouslySetInnerHTML={{
                      __html: String(introText).replace(/\n/g, "<br/>"),
                    }}
                  />
                )}
              </div>

              {panelFooter && (
                <div className="mt-5 pt-4 border-t text-xs text-gray-500">
                  {panelFooter}
                </div>
              )}
            </div>
          </Reveal>
        </div>

        <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-6">
          <Reveal variant="fadeUp" className="md:col-span-2">
            <div className="rounded-2xl border bg-white p-6 shadow-sm h-full">
              <div className="flex items-center justify-between gap-4">
                <h4 className="text-lg font-bold text-gray-900">
                  {capabilitiesTitle}
                </h4>
                <span className="text-xs uppercase tracking-widest text-gray-500">
                  {capabilitiesMeta}
                </span>
              </div>

              <div className="mt-5 grid grid-cols-1 sm:grid-cols-3 gap-3">
                {points.slice(0, 3).map((point: any, idx: number) => {
                  const Icon = iconMap[point.icon || point.iconName] || Building2;

                  return (
                    <div
                      key={`${point.title || "capability"}-${idx}`}
                      className="rounded-xl border bg-gray-50 px-4 py-4"
                    >
                      <div className="h-10 w-10 rounded-2xl bg-white border flex items-center justify-center text-red-700">
                        <Icon className="h-5 w-5" />
                      </div>
                      <div className="mt-3 text-sm font-bold text-gray-900">
                        {point.title}
                      </div>
                      <p className="mt-2 text-sm text-gray-700 text-justify leading-relaxed">
                        {point.desc || point.description}
                      </p>
                    </div>
                  );
                })}
              </div>
            </div>
          </Reveal>

          <Reveal variant="fadeUp" delayMs={150}>
            <div className="rounded-2xl shadow-sm border h-full bg-white">
              <div className="flex h-full min-h-[220px] flex-col items-center justify-center p-6 text-center">
                <p className="max-w-md text-sm text-gray-700 leading-relaxed">
                  <LinkText text={brandDesc} />
                </p>

                <div className="mt-4 flex flex-wrap justify-center gap-2">
                  {brandTags.map((tag: string) => (
                    <span
                      key={tag}
                      className="text-xs px-3 py-1 rounded-full border bg-gray-50"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

export default DivisionIntro;
